import 'regenerator-runtime/runtime';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  PhVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { PhVorvertragsdaten, SpartenType, VertragsdatenRequestModel } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PhActions } from '../phActions';
import { IAppState } from '../appState';

const globalActions = new GlobalActions();
const phActions = new PhActions();

export enum VorbelegungPhRequestEventsEnum {
  VORBELEGUNG_PH_REQUEST_SUCCESSFULL = 'VORBELEGUNG_PH_REQUEST_SUCCESSFULL',
  VORBELEGUNG_PH_REQUEST_FAILED = 'VORBELEGUNG_PH_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.PH));
  yield takeEvery(
    VorbelegungPhRequestEventsEnum.VORBELEGUNG_PH_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.PH),
  );
  yield takeEvery(
    VorbelegungPhRequestEventsEnum.VORBELEGUNG_PH_REQUEST_FAILED,
    safeSaga(errorCallback, App.PH),
  );
}

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>) {
  if (action.payload.appId !== App.PH) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* phDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.PH,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungPhRequestEventsEnum.VORBELEGUNG_PH_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungPhRequestEventsEnum.VORBELEGUNG_PH_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<PhVorvertragsdaten>) {
  const phVorbelegungdaten: PhVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.PRIVATHAFTPFLICHT,
    successAction.payload,
  ) as PhVorbelegungdaten;
  yield* phDatenVorbelegen(phVorbelegungdaten);
}

export function* phDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();
  const phVorbelegungdaten: PhVorbelegungdaten = vorbelegungdaten as PhVorbelegungdaten;
  const globalPut = GlobalPut.getInstance();
  yield put(phActions.familienstandChanged(phVorbelegungdaten.familienstand));
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.PH,
        state.angebotsId,
      ),
    );
  }
  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }
  if (!state.vertragsoptionen?.beibehalten) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.navigationRequested({
        appId: App.PH,
        subroute: '/eingabe',
      }),
    );
    yield put(new PhActions().produktDatenVorbelegt(phVorbelegungdaten));
  } else {
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(phVorbelegungdaten),
    );
  }
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.PH }));
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.PH }));
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested({
      appId: App.PH,
      subroute: '/eingabe',
    }),
  );
}
